import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';

export interface BottomTab {
    key: string;
    label: string;
    to: string;
    icon: LucideIcon;
    active: boolean;
    /** Small count dot (e.g. unread insights). */
    badge?: number;
    /** Render as a button (e.g. More opens a sheet) instead of a link. */
    onSelect?: () => void;
    /** With onSelect: whether the sheet it opens is showing. */
    expanded?: boolean;
}

export interface BottomTabBarProps {
    tabs: BottomTab[];
    /** Accessible name for the nav landmark. */
    label: string;
}

/**
 * Floating phone tab bar (< md). Active tab expands into a teal pill with
 * icon + label; inactive tabs are icon-only circles carrying aria-label.
 */
const cls = (active: boolean): string =>
    cn(
        'relative flex h-12 items-center justify-center gap-2 rounded-full transition-[background-color,color] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1FB7A6] focus-visible:ring-offset-2 motion-reduce:transition-none',
        active ? 'w-full min-w-0 bg-[#1FB7A6] px-3 text-white' : 'w-11 text-[#0F2A44]/70 hover:bg-[#0F2A44]/[0.05] hover:text-[#0F2A44]',
    );

export function BottomTabBar({ tabs, label }: BottomTabBarProps) {
    return (
        <nav
            aria-label={label}
            data-testid="pa-bottom-tab-bar"
            className="fixed inset-x-3 bottom-[calc(0.5rem+env(safe-area-inset-bottom))] z-40 md:hidden print:hidden"
        >
            <ul className="flex h-16 items-center justify-between gap-1 rounded-full bg-white px-2 shadow-[0_8px_30px_rgba(15,42,68,0.16)] ring-1 ring-[#0F2A44]/[0.05]">
                {tabs.map(({ key, label: tabLabel, to, icon: Icon, active, badge, onSelect, expanded }) => (
                    <li key={key} className={cn('flex', active ? 'min-w-0 flex-1' : 'flex-none justify-center')}>
                        {onSelect ? (
                            <button
                                type="button"
                                onClick={onSelect}
                                aria-label={active ? undefined : tabLabel}
                                aria-haspopup="dialog"
                                aria-expanded={expanded ?? false}
                                data-testid={`pa-tab-${key}`}
                                className={cls(active)}
                            >
                            <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                            {active ? <span className="truncate text-[14px] font-semibold">{tabLabel}</span> : null}
                            {badge && badge > 0 ? (
                                <span aria-hidden="true" className="absolute end-1.5 top-1.5 h-2.5 w-2.5 rounded-full bg-[#F47C20] ring-2 ring-white" />
                            ) : null}
                            </button>
                        ) : (
                            <Link
                                to={to}
                                aria-label={active ? undefined : tabLabel}
                                aria-current={active ? 'page' : undefined}
                                data-testid={`pa-tab-${key}`}
                                className={cls(active)}
                            >
                            <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                            {active ? <span className="truncate text-[14px] font-semibold">{tabLabel}</span> : null}
                            {badge && badge > 0 ? (
                                <span aria-hidden="true" className="absolute end-1.5 top-1.5 h-2.5 w-2.5 rounded-full bg-[#F47C20] ring-2 ring-white" />
                            ) : null}
                            </Link>
                        )}
                    </li>
                ))}
            </ul>
        </nav>
    );
}
